import type React from 'react'
import NextLink, { LinkProps } from 'next/link'
import { useRouter } from 'next/router'
import { memo } from 'react'

import { css, CSSObject } from '@emotion/react'

import { useMarket } from '@/utils/multi-markets/context'
import { toTranslatedLink } from '@/utils/i18n/enhanced-router'
import { focusVisible, underlineHover } from '@/styles/mixins'
import { Text1Styles } from '@/styles/typography'

interface CustomLinkProps extends LinkProps {
  isCustomized?: boolean
  css?: CSSObject
  legacy?: boolean
  prefetch?: boolean
}

const Link = (props: React.PropsWithChildren<CustomLinkProps>) => {
  const router = useRouter()
  const { locale, defaultLocale } = useMarket()
  const {
    href,
    as,
    isCustomized = false,
    legacy = true,
    prefetch = false,
    ...rest
  } = props

  const link = toTranslatedLink(href, as, router, locale, defaultLocale)

  return (
    <NextLink
      href={link.href}
      as={link.as}
      locale={false}
      prefetch={prefetch}
      {...rest}
      css={isCustomized ? pathLink : null}
      legacyBehavior={legacy}
    >
      {props.children}
    </NextLink>
  )
}

const pathLink = css`
  ${focusVisible};
  ${Text1Styles};
  ${underlineHover};

  color: var(--text-color);
  &:hover {
    color: var(--hover-color);
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid var(--focus-outline-color);
    outline-offset: 2px;
  }
  &:active {
    color: var(--active-color);
  }
`
const pathLinkClass = pathLink.name

export default memo(Link)
